import InfiniteScroll from 'react-infinite-scroll-component';
import { AxiosError } from 'axios';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import DocumentsHeader from '@/modules/admin-dashboard/components/DocumentsHeader.tsx';
import DocumentItem from '@/modules/admin-dashboard/components/items/DocumentItem.tsx';
import DocumentsFolderItem from '@/modules/admin-dashboard/components/items/DocumentsFolderItem.tsx';
import { findNodeById, findNodePath } from '@/modules/admin-dashboard/helpers/documents.ts';
import { useFoldersTreeQuery } from '@/modules/admin-dashboard/queries/documents-folders.ts';
import { useGetDocumentsListQuery } from '@/modules/admin-dashboard/queries/documents.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { BreadcrumbProps, Breadcrumbs, EmptyListState, Icon, PageLayout, Spinner } from 'ui';
import { useParseNumericQueryParam } from 'ui/hooks';
import { ArrowShortRightGrayIcon, FolderIcon } from 'ui/icons';
import { DocumentSortBy } from 'compliance-api';

export const CURRENT_FOLDER_QK = 'folder_id';

export interface FiltersDocuments {
  archived?: boolean;
  from_date?: string;
  to_date?: string;
  filter_by_name?: string;
  filter_by_format?: string;
  sort_by?: DocumentSortBy;
}

interface DocumentsProps {
  clientId?: number;
}

const Documents = ({ clientId }: DocumentsProps) => {
  const businessId = useGetCurrentBusinessId();
  const [filters, setFilters] = useState<FiltersDocuments>({});
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    setFilters({});
  }, [businessId, clientId]);

  const folderId = useParseNumericQueryParam(CURRENT_FOLDER_QK);
  const navigate = useNavigate();

  const { data: treeData } = useFoldersTreeQuery({
    filter_by_client_id: clientId,
  });

  const currentFolder = useMemo(() => {
    if (!folderId) return null;
    return findNodeById(treeData?.tree || [], folderId);
  }, [folderId, treeData?.tree]);

  const folders = useMemo(() => {
    if (!folderId) return treeData?.tree || [];
    return currentFolder?.children || [];
  }, [folderId, currentFolder, treeData?.tree]);

  // Hide client selector if we're inside a folder that has a client_id
  // (backend will auto-inherit parent's client_id)
  const hideClientSelector = !!currentFolder?.client_id;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching, error } =
    useGetDocumentsListQuery({
      page: 1,
      page_size: 18,
      folder_id: folderId || undefined,
      ...filters,
      filter_by_client_id: clientId,
    });

  const isLoading = isFetching && !isFetchingNextPage;

  const isAxiosError = error && error instanceof AxiosError;
  const isAccessFolderError = isAxiosError && error?.response?.status === 400;

  const documents = (data?.pages || []).flatMap(({ data }) => data);

  const breadcrumbs = useMemo((): BreadcrumbProps['items'] => {
    const path = findNodePath(treeData?.tree || [], folderId || 0);

    const navigateParams = createSearchParams(location.search);

    return [
      {
        title: 'Documents',
        className: classNames('text-[24px] leading-[32px] font-serif', {
          'text-[#A3A3A3] cursor-pointer': path?.length,
        }),
        onClick: () => {
          navigateParams.delete(CURRENT_FOLDER_QK);
          navigate({
            pathname: location.pathname,
            search: navigateParams.toString(),
          });
        },
      },
      ...(path || []).map(({ name, id, color }) => ({
        title: (
          <div className="flex gap-2 items-center">
            <Icon Source={FolderIcon} rawColor={color} />
            <span>{name}</span>
          </div>
        ),
        className: 'text-[22px] leading-[32px] font-serif cursor-pointer',
        onClick: () => {
          navigateParams.set(CURRENT_FOLDER_QK, id.toString());
          navigate({
            pathname: location.pathname,
            search: navigateParams.toString(),
          });
        },
      })),
    ];
  }, [folderId, navigate, treeData?.tree]);

  if (isAccessFolderError && !!folderId) {
    params.delete(CURRENT_FOLDER_QK);
    setParams(params);
    // toast.error('This folder does not exist', {
    //   id: 'access-denied',
    // });
    return (
      <Navigate
        to={{ pathname: adminDashboardPaths.static.documents, search: params.toString() }}
        replace={false}
      />
    );
  }

  return (
    <PageLayout
      title={
        <Breadcrumbs
          items={breadcrumbs}
          className="[&>ol]:items-center"
          separator={
            <div className="w-6 h-6 flex items-center justify-center">
              <img src={ArrowShortRightGrayIcon} alt="sepa" />
            </div>
          }
        />
      }
      contentClassName={'!p-0'}
    >
      <DocumentsHeader
        filters={filters}
        setFilters={setFilters}
        clientId={clientId}
        hideClientSelector={hideClientSelector}
      />
      <div className="flex-1 px-3 sm:px-6 py-4">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4 mb-4">
          {folders.map(folder => (
            <DocumentsFolderItem
              key={folder.id}
              folder={folder}
              clientId={clientId}
              hideClientSelector={hideClientSelector}
            />
          ))}
        </div>
        {isLoading && (
          <div className="w-full h-full flex justify-center items-center">
            <Spinner size="lg" />
          </div>
        )}

        <EmptyListState
          title="This folder is empty"
          description={
            folderId
              ? 'Add files or create subfolders to organise your content.'
              : 'Upload your documents to view and manage them in one place.'
          }
          isLoading={isLoading}
          itemsCount={documents.length}
          isSearchUsed={!isEmpty(filters)}
        />

        {!isLoading && !!documents.length && (
          <InfiniteScroll
            next={fetchNextPage}
            hasMore={!!hasNextPage}
            loader={
              <div className="h-[100px] flex justify-center items-center">
                <Spinner size="md" />
              </div>
            }
            dataLength={documents.length}
            className="space-y-3"
            scrollableTarget="scrollable"
          >
            {documents.map(doc => (
              <DocumentItem
                key={doc.id}
                doc={doc}
                clientId={clientId}
                hideClientSelector={hideClientSelector}
              />
            ))}
          </InfiniteScroll>
        )}
      </div>
    </PageLayout>
  );
};

export default Documents;
